// EntitlementModal — Cluster C.3.
//
// Rendered ONCE at the App root (mounted by <EntitlementProvider>'s
// global state). Whenever any component calls useEntitlementGate().check(),
// this modal opens with the right view per routeOutcome().
//
// UX principles:
//   - Radix Dialog primitive → accessibility for free (focus trap,
//     Escape close, click-outside, ARIA labelling).
//   - framer-motion `AnimatePresence` slides between views (Q5.4 view 1
//     → view 2 has a polished horizontal transition).
//   - Brand-consistent: each view has an `accent` color from viewConfig
//     driving the icon-circle tint + primary CTA bg.
//   - Mobile-first: bottom-sheet style on small screens, centered card
//     on md+ (Tailwind responsive utilities on DialogContent).
//   - Quote view shows real price (calls /alacarte/quote on demand),
//     wallet credit if applicable, fee-only flag for medicine delivery.
//   - No telemetry duplication: the context fires `entitlement_user_action`
//     events; this component is purely presentation.
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Loader2, X, ArrowRight, Wallet } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogPortal,
  DialogOverlay,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { useEntitlementModalApi, VIEWS } from '@/contexts/EntitlementContext';
import { VIEW_CONFIG, resolve, prettyPlan } from './viewConfig';

// Animation variants (~180ms slide) — slick but not slow.
const slideVariants = {
  enter: { opacity: 0, x: 24 },
  center: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -24 },
};
const slideTransition = { duration: 0.18, ease: 'easeOut' };

export default function EntitlementModal() {
  const api = useEntitlementModalApi();
  const navigate = useNavigate();

  const open = api.view !== VIEWS.CLOSED;
  const config = VIEW_CONFIG[api.view] || null;

  // Loading view (initial check in flight) doesn't have a config entry —
  // render a spinner-only state. Same for closed (won't render at all).
  const isLoading = api.view === VIEWS.LOADING;

  const handlePrimary = () => {
    switch (api.view) {
      case VIEWS.NOT_SUBSCRIBED:
        api.dismiss('upgraded');
        navigate('/plans');
        return;
      case VIEWS.PAST_DUE:
        api.dismiss('blocked_view');
        navigate('/profile');
        return;
      case VIEWS.PINCODE_UNSERVED:
      case VIEWS.SERVICE_INACTIVE:
        api.dismiss('blocked_view');
        return;
      case VIEWS.UPGRADE_REQUIRED:
        api.dismiss('upgraded');
        navigate('/plans');
        return;
      case VIEWS.CAP_UPGRADE:
        // Upgrade nudge primary → /plans (preselect target tier as
        // search param so PlansPage can highlight; PlansPage doesn't
        // read it yet — harmless until C.5 adds the highlight wiring).
        api.dismiss('upgraded');
        navigate(
          api.decision?.upgrade_to_tier
            ? `/plans?highlight=${api.decision.upgrade_to_tier}`
            : '/plans'
        );
        return;
      case VIEWS.CAP_ALACARTE: {
        // Navigate to à la carte checkout (Cluster C.4 will build this
        // page; until then it 404s gracefully). Lock_id from the quote
        // is the handoff token.
        const lockId = api.quote?.lock_id;
        api.dismiss('chose_alacarte');
        if (lockId) {
          navigate(`/alacarte/checkout?lock_id=${lockId}`);
        } else {
          navigate('/plans');
        }
        return;
      }
      case VIEWS.ERROR:
        api.retry();
        return;
      default:
        api.dismiss('abandoned');
    }
  };

  const handleSecondary = () => {
    if (api.view === VIEWS.CAP_UPGRADE) {
      // Q5.4 transition: "See à la carte price" → fetch quote → view 2
      api.dismissUpgradeShowAlacarte();
      return;
    }
    api.dismiss('abandoned');
  };

  const handleTertiary = () => {
    // CAP_UPGRADE has 3 buttons: upgrade / view-alacarte / not-now.
    api.dismiss('abandoned');
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) api.dismiss('abandoned'); }}>
      <DialogPortal>
        <DialogOverlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogContent
          className="
            fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2
            w-[calc(100%-1.5rem)] max-w-md
            sm:rounded-2xl
            data-[state=open]:animate-in data-[state=closed]:animate-out
            data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95
            duration-200
            p-0 overflow-hidden
            border-0 outline-none
          "
          style={{ background: 'var(--surface)' }}
          data-testid="entitlement-modal"
        >
          {/* Hidden a11y titles — visible title is rendered inside view bodies */}
          <DialogTitle className="sr-only">Service eligibility</DialogTitle>
          <DialogDescription className="sr-only">
            Information about whether you can book this service right now
          </DialogDescription>

          {/* Close button — top right, brand-subtle */}
          <button
            type="button"
            onClick={() => api.dismiss('abandoned')}
            aria-label="Close"
            className="absolute right-3 top-3 z-10 p-1.5 rounded-full hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
            style={{ color: 'var(--text-light)' }}
            data-testid="entitlement-modal-close"
          >
            <X size={18} />
          </button>

          {isLoading ? (
            <LoadingView />
          ) : config ? (
            <AnimatePresence mode="wait">
              <motion.div
                key={api.view}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={slideTransition}
                className="px-6 pt-8 pb-6"
                data-testid={`entitlement-view-${api.view}`}
              >
                <ViewBody
                  view={api.view}
                  config={config}
                  decision={api.decision}
                  serviceMeta={api.serviceMeta}
                  quote={api.quote}
                  errorMessage={api.errorMessage}
                />

                <ButtonRow
                  view={api.view}
                  config={config}
                  decision={api.decision}
                  onPrimary={handlePrimary}
                  onSecondary={handleSecondary}
                  onTertiary={handleTertiary}
                />
              </motion.div>
            </AnimatePresence>
          ) : null}
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}

// ─── Loading view (initial /entitlement/check round-trip) ───────────────

function LoadingView() {
  return (
    <div className="px-6 py-10 flex flex-col items-center" data-testid="entitlement-loading">
      <Loader2 size={28} className="animate-spin mb-3" style={{ color: 'var(--brand-primary)' }} />
      <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
        Checking eligibility...
      </p>
    </div>
  );
}

// ─── ViewBody — accent header + headline + helper paragraph + extras ───

function ViewBody({ view, config, decision, serviceMeta, quote, errorMessage }) {
  const Icon = config.icon;
  const accent = config.accent;
  const eyebrow = resolve(config.eyebrow, decision, serviceMeta, quote, errorMessage);
  const headline = resolve(config.headline, decision, serviceMeta, quote, errorMessage);
  const helperBody = resolve(config.helperBody, decision, serviceMeta, quote, errorMessage);

  // Tinted icon background (15% alpha of accent — works for any hex).
  const iconBg = `${accent}1A`; // 0x1A = 26/255 ≈ 10%

  return (
    <div>
      {/* Icon circle */}
      <div className="flex justify-center mb-4">
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center"
          style={{ background: iconBg }}
        >
          <Icon
            size={26}
            style={{ color: accent }}
            className={view === VIEWS.ALACARTE_LOADING ? 'animate-spin' : ''}
            aria-hidden="true"
          />
        </div>
      </div>

      {/* Eyebrow + headline */}
      <div className="text-center mb-4">
        {eyebrow && (
          <p
            className="text-xs uppercase tracking-wider font-semibold mb-1.5"
            style={{ color: accent, letterSpacing: '0.08em' }}
          >
            {eyebrow}
          </p>
        )}
        <h2
          className="text-lg font-bold leading-tight"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          {headline}
        </h2>
      </div>

      {/* Helper body */}
      {helperBody && (
        <p
          className="text-sm leading-relaxed text-center mb-5"
          style={{ color: 'var(--text-muted)' }}
        >
          {helperBody}
        </p>
      )}

      {/* View-specific inset content (quote details, plan diff, etc.) */}
      <ViewInset
        view={view}
        decision={decision}
        serviceMeta={serviceMeta}
        quote={quote}
      />
    </div>
  );
}

// ─── ViewInset — view-specific structured panels ───────────────────────

function ViewInset({ view, decision, serviceMeta, quote }) {
  // Upgrade nudge (View 1) — show the gain in concrete terms.
  if (view === VIEWS.CAP_UPGRADE && decision?.upgrade_to_tier) {
    return (
      <div
        className="rounded-xl p-4 mb-4 flex items-center justify-between gap-3"
        style={{ background: 'var(--bg-main)' }}
        data-testid="entitlement-upgrade-card"
      >
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#4A90E2' }}>
            Upgrade
          </p>
          <p className="text-sm font-semibold mt-0.5">
            {prettyPlan(decision.upgrade_to_tier)}
          </p>
          {decision.upgrade_inclusions_delta > 0 && (
            <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
              +{decision.upgrade_inclusions_delta} more {serviceMeta?.display_name || 'this service'} per month
            </p>
          )}
        </div>
        <ArrowRight size={18} style={{ color: '#4A90E2' }} aria-hidden="true" />
      </div>
    );
  }

  // À la carte quote (View 2) — show the locked price, wallet credit
  // applied if any, fee-only annotation for medicine delivery.
  //
  // All three breakdown rows render in the user's PAYMENT currency
  // (Stripe charges quoted_price_display in currency_display) so the
  // math visually adds up. Wallet ledger is INR-canonical server-side,
  // but the user sees the same currency as their card charge.
  if (view === VIEWS.CAP_ALACARTE && quote) {
    const subtotal = quote.subtotal_before_wallet_display ?? quote.quoted_price_display;
    const wallet = quote.wallet_credit_applied_display ?? 0;
    const youPay = quote.quoted_price_display;
    const symbol = quote.currency_symbol || '$';
    const fmt = (n) => `${symbol}${Number(n).toFixed(2)}`;
    return (
      <div
        className="rounded-xl p-4 mb-4 space-y-2"
        style={{ background: 'var(--bg-main)' }}
        data-testid="entitlement-quote-card"
      >
        {/* Subtotal row only when wallet applied — otherwise "You pay"
            speaks for itself */}
        {wallet > 0 && (
          <div className="flex justify-between text-xs" style={{ color: 'var(--text-muted)' }}>
            <span>Subtotal</span>
            <span data-testid="quote-subtotal">{fmt(subtotal)}</span>
          </div>
        )}
        {wallet > 0 && (
          <div className="flex justify-between items-center text-xs">
            <span className="flex items-center gap-1.5" style={{ color: '#16A34A' }}>
              <Wallet size={12} aria-hidden="true" />
              Wallet credit
            </span>
            <span style={{ color: '#16A34A' }} data-testid="quote-wallet-applied">
              − {fmt(wallet)}
            </span>
          </div>
        )}
        <div
          className="flex justify-between items-baseline pt-1"
          style={{ borderTop: wallet > 0 ? '1px dashed var(--border-color)' : 'none' }}
        >
          <span className="text-sm font-semibold">You pay</span>
          <span
            className="text-2xl font-bold"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="quote-you-pay"
          >
            {fmt(youPay)}
          </span>
        </div>
        {quote.is_fee_only && quote.fee_label && (
          <p className="text-xs pt-1.5" style={{ color: 'var(--text-muted)' }}>
            * {quote.fee_label} only — medicines charged at MRP at delivery.
          </p>
        )}
        <p className="text-xs text-center pt-1" style={{ color: 'var(--text-light)' }}>
          Price locked for 15 minutes
        </p>
      </div>
    );
  }

  return null;
}

// ─── ButtonRow — primary / secondary / tertiary CTAs ───────────────────

function ButtonRow({ view, config, decision, onPrimary, onSecondary, onTertiary }) {
  const primaryLabel = resolve(config.primaryLabel, decision);
  const secondaryLabel = config.secondaryLabel;
  const tertiaryLabel = config.tertiaryLabel;
  const accent = config.accent;

  if (view === VIEWS.ALACARTE_LOADING) return null;

  return (
    <div className="space-y-2 mt-2">
      {primaryLabel && (
        <button
          type="button"
          className="
            w-full px-4 py-2.5 rounded-xl font-semibold text-sm
            transition-all active:scale-[0.98]
            shadow-sm
          "
          style={{
            background: accent,
            color: 'white',
          }}
          onClick={onPrimary}
          data-testid={`entitlement-${view}-primary-btn`}
        >
          {primaryLabel}
        </button>
      )}
      {secondaryLabel && (
        <button
          type="button"
          className="
            w-full px-4 py-2.5 rounded-xl font-semibold text-sm
            transition-colors
            border
          "
          style={{
            background: 'transparent',
            color: 'var(--text-base, var(--text-strong))',
            borderColor: 'var(--border-color, #e5e7eb)',
          }}
          onClick={onSecondary}
          data-testid={`entitlement-${view}-secondary-btn`}
        >
          {secondaryLabel}
        </button>
      )}
      {tertiaryLabel && (
        <button
          type="button"
          className="
            w-full text-xs font-medium pt-1
            transition-colors
          "
          style={{ color: 'var(--text-light)' }}
          onClick={onTertiary}
          data-testid={`entitlement-${view}-tertiary-btn`}
        >
          {tertiaryLabel}
        </button>
      )}
    </div>
  );
}
